import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ChevronRight, FileText } from 'lucide-react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { getDoc, groupedDocs, validateDocs } from '../docs/registry';
import CONTENT from '../docs/content';
import usePageTitle from '../hooks/usePageTitle';
import useCanonical from '../hooks/useCanonical';

const BRAND_BLUE = '#2B4B6A';

// One long prose chain, tuned once so every page inherits the look.
const PROSE = [
  'prose prose-slate max-w-none',
  'prose-headings:font-bold prose-headings:text-gray-900',
  'prose-h2:text-xl prose-h2:mt-9 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100',
  'prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-gray-700',
  'prose-li:text-[15px] prose-li:text-gray-700',
  'prose-a:font-medium prose-a:no-underline hover:prose-a:underline',
  'prose-code:text-[13px] prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5',
  'prose-code:rounded prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:text-[13px]',
  'prose-table:text-[14px] prose-th:text-left',
].join(' ');

const Sidebar = ({ activeSlug }) => (
  <nav className="lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto pr-2">
    {groupedDocs().map(({ section, pages }) => (
      <div key={section} className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">{section}</p>
        <ul className="space-y-0.5">
          {pages.map((p) => {
            const active = p.slug === activeSlug;
            return (
              <li key={p.slug}>
                <Link
                  to={`/docs/${p.slug}`}
                  aria-current={active ? 'page' : undefined}
                  className={`block text-sm py-1.5 pl-3 border-l-2 transition-colors ${
                    active
                      ? 'font-semibold border-current'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={active ? { color: BRAND_BLUE, borderColor: BRAND_BLUE } : undefined}
                >
                  {p.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </nav>
);

const DocPage = () => {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const doc = getDoc(slug);
  const Body = CONTENT[slug];

  usePageTitle(doc ? `${doc.title} | Trados Docs` : 'Documentation | Trados');
  useCanonical(doc ? `/docs/${slug}` : null);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { if (process.env.NODE_ENV !== 'production') validateDocs(); }, []);

  // TechArticle JSON-LD — this is what earns docs pages rich results
  useEffect(() => {
    if (!doc) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'doc-jsonld';
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: doc.title,
      description: doc.description,
      articleSection: doc.section,
      dateModified: doc.updated,
      url: `https://www.trados.app/docs/${doc.slug}`,
      publisher: { '@type': 'Organization', name: 'Trados', url: 'https://www.trados.app' },
    });
    document.head.appendChild(el);
    return () => { document.getElementById('doc-jsonld')?.remove(); };
  }, [doc]);

  if (!doc || !Body) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <PublicHeader />
        <div className="flex-1 max-w-3xl mx-auto px-6 py-24 text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900">Page not found</h1>
          <p className="text-sm text-gray-500 mt-1.5">That documentation page does not exist.</p>
          <Link to="/docs" className="inline-block mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: BRAND_BLUE }}>Browse documentation</Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <div className="flex-1 w-full max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <Sidebar activeSlug={slug} />
          <article>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
              <Link to="/docs" className="hover:text-gray-600">Docs</Link>
              <ChevronRight className="w-3 h-3" />
              <span>{doc.section}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{doc.title}</h1>
            <p className="text-gray-500 mt-2 mb-1">{doc.description}</p>
            {doc.updated && <p className="text-xs text-gray-400 mb-7">Updated {doc.updated}</p>}
            <div className={PROSE}><Body /></div>
          </article>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default DocPage;
